import { useEffect, useMemo, useState } from 'react';
import useCurrentWalletAdapter from '../useCurrentWalletAdapter';
import { usePrivyWalletContext } from 'src/providers/wallets/PrivyWalletProvider';
import { fetchUserStatsAccount, PublicKey } from '@drift-labs/sdk';
import { useQuery } from '@tanstack/react-query';
import useDriftClient from '../useDriftClient';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import {
	convertPrivyEmbeddedWalletToIWalletV2,
	convertSolanaWalletAdapterToIWalletV2,
	UiEnhancedIWalletV2,
} from 'src/utils/wallets/IWalletV2';
import useCurrentUserAccount from '../useCurrentUserAccount';
import { COMMON_UI_UTILS } from '@drift/common';
import { BaseMessageSignerWalletAdapter } from '@solana/wallet-adapter-base';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import useCurrentSettings from '../useCurrentSettings';

function useFetchDriftUserStats(
	authority: PublicKey | string | null | undefined
) {
	const connection = useDriftStore((s) => s.connection.current);
	const driftClient = useDriftClient();

	return useQuery({
		queryKey: ['driftAccounts', authority?.toString()],
		queryFn: async () => {
			if (!authority || !connection || !driftClient?.program) {
				return null;
			}

			const userStatsAccount = await fetchUserStatsAccount(
				connection,
				driftClient.program,
				new PublicKey(authority)
			);

			return userStatsAccount || null; // can't return undefined
		},
		enabled: !!authority && !!connection && !!driftClient?.program,
		staleTime: 1000 * 60 * 5, // 5 minutes
		gcTime: 1000 * 60 * 10, // 10 minutes
	});
}

/**
 * This hook returns the main authority and the preferred signer of transactions.
 * The distinction between the two is that:
 * - The main authority is the wallet that owns the actual Drift accounts.
 * - The preferred signer is a Privy embedded wallet delegate (if it exists), otherwise it will be the main authority itself. Use the preferred signer only for delegatable actions (e.g. all trading actions, swaps)
 */
export const useWalletsV2 = (onlyRequireMessageSigning = false) => {
	const isEmbeddedWalletFeatureFlagEnabled = useFeatureFlagStore((s) =>
		s.flagEnabled('ENABLE_EMBEDDED_WALLET')
	);
	const isTransactionAutoSigningDisabled = useFeatureFlagStore((s) =>
		s.flagEnabled('DISABLE_TRANSACTION_AUTO_SIGNING')
	);
	const [currentSettings] = useCurrentSettings();
	const isEmbeddedWalletFeatureEnabled =
		isEmbeddedWalletFeatureFlagEnabled && currentSettings.enableSignlessTrading;
	const { embeddedWallet, isPrivyNativeAuth } = usePrivyWalletContext();
	const solanaWalletAdapter = useCurrentWalletAdapter();
	const driftClient = useDriftClient();

	const [authorityWallet, setAuthorityWallet] =
		useState<UiEnhancedIWalletV2 | null>(null);
	const [authorityWalletNoFeePayer, setAuthorityWalletNoFeePayer] =
		useState<UiEnhancedIWalletV2 | null>(null);
	const [preferredTradingSignerWallet, setPreferredTradingSignerWallet] =
		useState<UiEnhancedIWalletV2 | null>(null);

	const { data: solanaWalletAdapterUserStatsAccount } = useFetchDriftUserStats(
		solanaWalletAdapter.publicKey
	);
	const { data: embeddedWalletUserStatsAccount } = useFetchDriftUserStats(
		embeddedWallet?.address
	);

	const currentUserAccount = useCurrentUserAccount();
	const embeddedWalletPublicKey = useMemo(() => {
		if (!embeddedWallet) return null;
		return new PublicKey(embeddedWallet.address);
	}, [embeddedWallet]);

	const isSolanaWalletAdapterTheDelegateOfCurrentUserAccount =
		solanaWalletAdapter.publicKey &&
		currentUserAccount &&
		currentUserAccount.delegate.equals(solanaWalletAdapter.publicKey);
	const currentUserAccountAuthority = currentUserAccount?.authority;

	const isEmbeddedWalletConnected = !!embeddedWallet;
	const isSolanaWalletAdapterConnected = solanaWalletAdapter.connected;
	const adapterHasDriftAccount = !!solanaWalletAdapterUserStatsAccount;
	const embeddedWalletHasDriftAccount = !!embeddedWalletUserStatsAccount;
	const isEmbeddedWalletTheDelegateOfCurrentUserAccount =
		embeddedWalletPublicKey &&
		currentUserAccount &&
		currentUserAccount.delegate.equals(embeddedWalletPublicKey);
	const isDelegatedPrivyNativeAccountSelection =
		!!currentUserAccount?.isDelegatedTo &&
		!!currentUserAccountAuthority &&
		!!embeddedWalletPublicKey &&
		!currentUserAccountAuthority.equals(embeddedWalletPublicKey);

	const bothNoDriftAccountAndAdapterIsConnected =
		!adapterHasDriftAccount &&
		!embeddedWalletHasDriftAccount &&
		isSolanaWalletAdapterConnected;

	const isEmbeddedWalletThePreferredTradingSigner = useMemo(
		() =>
			!!(
				embeddedWallet &&
				preferredTradingSignerWallet?.publicKey.equals(
					new PublicKey(embeddedWallet.address)
				)
			),
		[embeddedWallet, preferredTradingSignerWallet]
	);

	useEffect(() => {
		if (!driftClient) {
			setAuthorityWallet(null);
			setAuthorityWalletNoFeePayer(null);
			setPreferredTradingSignerWallet(null);
			return;
		}

		if (isPrivyNativeAuth && embeddedWallet) {
			const embeddedWalletIWalletV2 = convertPrivyEmbeddedWalletToIWalletV2(
				embeddedWallet,
				driftClient
			);
			const embeddedWalletNoFeePayerIWalletV2 =
				convertPrivyEmbeddedWalletToIWalletV2(
					embeddedWallet,
					driftClient,
					false
				);

			// For Privy-native accounts that get delegated to, authority and signing
			// wallet need to be distinct so Swift uses delegate encoding.
			if (
				isDelegatedPrivyNativeAccountSelection &&
				currentUserAccountAuthority
			) {
				const authorityIWallet = COMMON_UI_UTILS.createPlaceholderIWallet(
					currentUserAccountAuthority
				);

				setAuthorityWallet(
					convertSolanaWalletAdapterToIWalletV2(
						authorityIWallet as BaseMessageSignerWalletAdapter,
						driftClient,
						true
					)
				);
				// For no-gas-sponsor flows, the embedded wallet still signs manually.
				setAuthorityWalletNoFeePayer(embeddedWalletNoFeePayerIWalletV2);
				setPreferredTradingSignerWallet(embeddedWalletIWalletV2);
				return;
			}

			setAuthorityWallet(embeddedWalletIWalletV2);
			setAuthorityWalletNoFeePayer(embeddedWalletNoFeePayerIWalletV2);
			setPreferredTradingSignerWallet(embeddedWalletIWalletV2);
			return;
		}

		if (solanaWalletAdapter.connected) {
			// when Solana Wallet Adapter is the delegate of the current user account (i.e. my connected wallet is controlling another account)
			if (isSolanaWalletAdapterTheDelegateOfCurrentUserAccount) {
				const solanaWalletAdapterIWalletV2 =
					convertSolanaWalletAdapterToIWalletV2(
						solanaWalletAdapter,
						driftClient,
						false
					);

				const authorityIWallet = COMMON_UI_UTILS.createPlaceholderIWallet(
					currentUserAccountAuthority
				);

				// we use a placeholder wallet, since user don't have actual wallet authority, so if UI uses this wallet to sign message/transaction in this scenario, it should be a bug
				const emulatedWalletAdapterIWalletV2 =
					convertSolanaWalletAdapterToIWalletV2(
						authorityIWallet as BaseMessageSignerWalletAdapter,
						driftClient,
						false
					);
				setAuthorityWallet(emulatedWalletAdapterIWalletV2);
				setAuthorityWalletNoFeePayer(emulatedWalletAdapterIWalletV2);
				setPreferredTradingSignerWallet(solanaWalletAdapterIWalletV2);
				return;
			}

			const isTransactionSigningDisabled =
				isTransactionAutoSigningDisabled && !onlyRequireMessageSigning;

			// if embedded wallet feature is disabled, or requires transaction signing but its disabled,
			// fallback to using the Solana Wallet Adapter as the preferred trading signer
			if (!isEmbeddedWalletFeatureEnabled || isTransactionSigningDisabled) {
				const solanaWalletAdapterIWalletV2 =
					convertSolanaWalletAdapterToIWalletV2(
						solanaWalletAdapter,
						driftClient,
						isEmbeddedWalletConnected
					);
				const solanaWalletAdapterNoFeePayerIWalletV2 =
					convertSolanaWalletAdapterToIWalletV2(
						solanaWalletAdapter,
						driftClient,
						false
					);
				setAuthorityWallet(solanaWalletAdapterIWalletV2);
				setAuthorityWalletNoFeePayer(solanaWalletAdapterNoFeePayerIWalletV2);
				setPreferredTradingSignerWallet(solanaWalletAdapterIWalletV2);
				return;
			}

			// when Solana Wallet Adapter is the main authority
			if (adapterHasDriftAccount || bothNoDriftAccountAndAdapterIsConnected) {
				const solanaWalletAdapterIWalletV2 =
					convertSolanaWalletAdapterToIWalletV2(
						solanaWalletAdapter,
						driftClient,
						isEmbeddedWalletConnected
					);

				setAuthorityWallet(solanaWalletAdapterIWalletV2);

				// When embedded wallet is the preferred signer, authorityWallet uses fee payer.
				// For FeePayerError fallback, we need a wallet that signs without fee payer.
				if (embeddedWallet && isEmbeddedWalletTheDelegateOfCurrentUserAccount) {
					setPreferredTradingSignerWallet(
						convertPrivyEmbeddedWalletToIWalletV2(embeddedWallet, driftClient)
					);
				} else {
					setPreferredTradingSignerWallet(solanaWalletAdapterIWalletV2);
				}

				setAuthorityWalletNoFeePayer(
					convertSolanaWalletAdapterToIWalletV2(
						solanaWalletAdapter,
						driftClient,
						false
					)
				);

				// in the case where both wallets are connected and both have Drift accounts,
				// we prioritize the Solana Wallet Adapter as the main authority

				return;
			}
		}

		// any other case, we set both to null
		setAuthorityWallet(null);
		setAuthorityWalletNoFeePayer(null);
		setPreferredTradingSignerWallet(null);
	}, [
		driftClient,
		currentUserAccountAuthority,
		isPrivyNativeAuth,
		isEmbeddedWalletFeatureEnabled,
		isTransactionAutoSigningDisabled,
		isSolanaWalletAdapterTheDelegateOfCurrentUserAccount,
		adapterHasDriftAccount,
		bothNoDriftAccountAndAdapterIsConnected,
		solanaWalletAdapter,
		embeddedWallet,
		embeddedWalletHasDriftAccount,
		isEmbeddedWalletConnected,
		isEmbeddedWalletTheDelegateOfCurrentUserAccount,
		isDelegatedPrivyNativeAccountSelection,
		onlyRequireMessageSigning,
	]);

	return {
		authorityWallet,
		authorityWalletNoFeePayer,
		preferredTradingSignerWallet,
		isEmbeddedWalletThePreferredTradingSigner,
		isEmbeddedWalletTheDelegateOfCurrentUserAccount,
	};
};
